import { DocumentStoreType } from "client/Api/Documents/common";
import {
	OpenFilePossibilitiesProvider as WebDavOpenFilePossibilitiesProvider
} from "client/Features/Documents/WebDav/openFilePossibilitiesProvider";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import {
	calculateForExtension as calculateSharePointOpenPossibilitiesForExtension,
	ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
} from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import {
	BookmarkModel,
	SharePointBookmarkModel,
	WebDavBookmarkModel
} from "client/Api/Documents/Bookmarks/bookmarks";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";

interface OpenPossibilities {
	folder: boolean;
	wopi: boolean;
	browser: boolean;
	imagePreviewDialog: boolean;
	local: boolean;
	sharePointViewer: boolean;
	location: boolean;
	externalUrl: boolean;
}

class OpenBookmarkPossibilitiesProvider {

	private readonly _webDavPossibilities: WebDavOpenFilePossibilitiesProvider;

	constructor() {
		this._webDavPossibilities = new WebDavOpenFilePossibilitiesProvider();
	}

	public get(bookmark: BookmarkModel): OpenPossibilities {
		switch (bookmark.storeType) {
			case DocumentStoreType.SharePoint:
				return this.getForSharePoint(bookmark);
			case DocumentStoreType.WebDav:
				return this.getForWebDav(bookmark);
			default:
				return this.unknownBookmark(bookmark);
		}
	}

	private getForSharePoint(bookmark: SharePointBookmarkModel): OpenPossibilities {
		if (bookmark.itemType === DocumentItemType.Folder) {
			return this.getForFolder();
		}

		const extension = bookmark.itemType === DocumentItemType.OneNoteNotebook
			? ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
			: bookmark.extension;
		const {
			canBeOpenedInTheBrowser,
			canBeOpenedOnline,
			canBeOpenedLocally,
			canBeOpenedInImagePreviewDialog,
			canBeOpenedInSharePointViewer,
			canBeOpenedAsExternalUrl
		} = calculateSharePointOpenPossibilitiesForExtension(extension);

		return {
			folder: false,
			browser: canBeOpenedInTheBrowser,
			imagePreviewDialog: canBeOpenedInImagePreviewDialog,
			wopi: canBeOpenedOnline,
			local: canBeOpenedLocally,
			sharePointViewer: canBeOpenedInSharePointViewer,
			externalUrl: canBeOpenedAsExternalUrl,
			location: true
		};
	}

	private getForWebDav(bookmark: WebDavBookmarkModel): OpenPossibilities {
		if (bookmark.itemType === DocumentItemType.Folder) {
			return this.getForFolder();
		}

		const { webDav: webDavConfiguration } = documentsConfiguration();

		if (!webDavConfiguration) {
			throw new Error("WebDav is not available");
		}

		const extension = bookmark.itemType === DocumentItemType.OneNoteNotebook
			? ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
			: bookmark.extension;

		const {
			browser,
			wopi,
			local,
			imagePreviewDialog,
			externalUrl
		} = this._webDavPossibilities.getForExtension(
			extension,
			webDavConfiguration
		);

		return {
			folder: false,
			browser,
			imagePreviewDialog,
			externalUrl,
			wopi,
			local,
			sharePointViewer: false,
			location: true
		};
	}

	private getForFolder(): OpenPossibilities {
		return {
			folder: true,
			browser: false,
			imagePreviewDialog: false,
			externalUrl: false,
			wopi: false,
			local: false,
			sharePointViewer: false,
			location: true
		};
	}

	private unknownBookmark(bookmark: never): never {
		throw new Error(`Unknown bookmark ${bookmark}`);
	}
}

export {
	OpenBookmarkPossibilitiesProvider,
	type OpenPossibilities
};
